import "./style.scss";
import { useMemo, useState } from "react";
import CloseSvg from "@/src/svg/CustomUtils/CloseSvg";
import ArrowSvg from "@/src/svg/CustomUtils/ArrowSvg";
import ProfileSvg from "@/src/svg/CustomUtils/ProfileSvg";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import { IApplyInfoComment } from "@/src/types/applicationOnline";
import { commentsRequests } from "@/src/api/commentsRequests";
import { sortCommentsNewestFirst } from "@/src/utils/officeAccountingMappers";
import { useFormatDateDisplay } from "@/src/hooks/useFormatDateDisplay";
import { parseCalcCommentText, wrapCommentWithCalcAmount } from "@/src/utils/calculateCommentMeta";

type Props = {
  title?: string;
  value: string;
  currency?: string;
  comments?: IApplyInfoComment[];
  accountingId?: number;
  onChange: (value: string) => void;
  onRefresh?: () => void | Promise<void>;
  onClose: () => void;
};

const TEXT_PLACEHOLDER = "—";

const stripCurrency = (raw: string) =>
  raw
    .replace(/USD|EUR|UAH/gi, "")
    .replace(/\s+/g, " ")
    .trim();

const parseInitialFormula = (raw: string) => {
  const cleaned = stripCurrency(raw);
  if (!cleaned) return "";
  return cleaned;
};

const evaluateFormula = (input: string): number | null => {
  const cleaned = input
    .trim()
    .replace(/^=/, "")
    .replace(/\s/g, "")
    .replace(/,/g, ".");

  if (!cleaned) return null;
  if (!/^[\d+\-*/.()]+$/.test(cleaned)) return null;

  try {
    const result = Function(`"use strict"; return (${cleaned})`)();
    return typeof result === "number" && Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
};

const formatSum = (n: number): string => {
  const [intPart, decPart] = n.toFixed(2).split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${withSpaces}.${decPart}`;
};

export const formatTuitionFeeDisplay = (raw: string, currency = "USD"): string => {
  const original = String(raw ?? "").trim();
  if (!original) return "";

  const formula = parseInitialFormula(original);
  const expression = formula.replace(/^=/, "").trim();
  const isFormula = original.trim().startsWith("=") || /[+\-*/()]/.test(expression.replace(/[\d\s.,]/g, ""));
  if (!isFormula) return original;

  const sum = evaluateFormula(formula);
  if (sum == null) return original;
  return `${formatSum(sum)} ${currency}`;
};

const CustomCalculatePopUp = ({
  title = "Select an option",
  value,
  currency = "USD",
  comments = [],
  accountingId,
  onChange,
  onRefresh,
  onClose,
}: Props) => {
  const formatDateDisplay = useFormatDateDisplay();
  // Input keeps the calculation expression; Sum shows the evaluated result separately.
  const [formula, setFormula] = useState(() => parseInitialFormula(value));
  const [noteValue, setNoteValue] = useState("");
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const displayedComments = useMemo(() => sortCommentsNewestFirst(comments), [comments]);
  const sumValue = useMemo(() => evaluateFormula(formula), [formula]);
  const formattedSum = sumValue != null ? formatSum(sumValue) : "";
  const amountLabel = formattedSum ? `${formattedSum} ${currency}` : "";

  const handleClear = () => {
    setFormula("");
    setNoteValue("");
  };

  const handleDone = async () => {
    if (sumValue == null || isSubmitting) return;

    const note = noteValue.trim();
    // Persist the expression itself (e.g. =100+500), not the evaluated 600.
    const nextValue = formula.trim();

    setIsSubmitting(true);
    try {
      if (note && accountingId != null && accountingId > 0) {
        // Freeze current Sum onto this comment only — not shared with other comments / input.
        const commentText = wrapCommentWithCalcAmount(note, amountLabel);
        const result = await commentsRequests.createAccountingComment({
          text: commentText,
          accounting_id: accountingId,
        });
        if (result == null) return;
        setNoteValue("");
        await onRefresh?.();
      }

      onChange(nextValue);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="custom-calculate-popup">
      <div className="custom-calculate-popup-title">
        <div className="custom-calculate-popup-title-text">
          <p>{title}</p>
        </div>
        <div className="custom-calculate-popup-title-close" onClick={onClose}>
          <CloseSvg />
        </div>
      </div>

      <div className="custom-calculate-popup-input">
        <input
          type="text"
          value={formula}
          onChange={(e) => setFormula(e.target.value)}
          placeholder="=0+0"
          disabled={isSubmitting}
        />
      </div>

      <div className="custom-calculate-popup-sum">
        <p>Sum:</p>
        <p>{formattedSum || "0.00"}</p>
      </div>

      <div
        className={`custom-calculate-popup-comments-toggle ${isCommentsOpen ? "open" : ""}`}
        onClick={() => setIsCommentsOpen((prev) => !prev)}
      >
        <p>Comments</p>
        <ArrowSvg />
      </div>

      {isCommentsOpen && displayedComments.length > 0 && (
        <div className="custom-calculate-popup-comments">
          {displayedComments.map((item) => {
            const { amount, body } = parseCalcCommentText(item.text || "");
            return (
              <div className="custom-calculate-popup-comments-item" key={item.id}>
                <div className="custom-calculate-popup-comments-item-header">
                  <div className="custom-calculate-popup-comments-item-meta">
                    <div className="custom-calculate-popup-comments-item-logo">
                      {item.commentator_photo ? <img src={item.commentator_photo} alt="" /> : <ProfileSvg />}
                    </div>
                    <p>{item.created_at ? formatDateDisplay(item.created_at, { dateOnly: true }) : TEXT_PLACEHOLDER}</p>
                  </div>
                  <p className="custom-calculate-popup-comments-item-amount">{amount || TEXT_PLACEHOLDER}</p>
                </div>
                <div className="custom-calculate-popup-comments-item-body">
                  <p>{body || TEXT_PLACEHOLDER}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={`custom-calculate-popup-note ${isCommentsOpen && displayedComments.length > 0 ? "compact" : ""}`}>
        <textarea
          placeholder="Note..."
          value={noteValue}
          onChange={(e) => setNoteValue(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="custom-calculate-popup-buttons">
        <BtnDefault onClick={handleClear} disabled={isSubmitting}>
          <p>Clear</p>
        </BtnDefault>
        <BtnDefault onClick={handleDone} disabled={isSubmitting || sumValue == null}>
          <p>Done</p>
        </BtnDefault>
      </div>
    </div>
  );
};

export default CustomCalculatePopUp;
